import { useEffect, useRef } from 'react';
import { Tree } from 'react-arborist';
import styled from 'styled-components';
import {
  LauncherWindowData,
  useLauncherWindowStore,
} from '../../../hooks/stores/launcherWindowStore';
import { UIData, useUIStore } from '../../../hooks/stores/uiStore';
import { ElementTreeDragCursor } from './ElementTreeDragCursor';
import { ElementTreeDragPreview } from './ElementTreeDragPreview';
import { ElementTreeNode } from './ElementTreeNode';
import { useScene } from '../../../context/sceneContext';
import {
  LauncherElementData,
  useLauncherElementsStore,
} from '../../../hooks/stores/launcherElementsStore';
import { launcherElementInfo } from '../../../info/launcherElementInfo';

export const ElementTree = ({
  height,
  searchQuery,
}: {
  height?: number;
  searchQuery: string;
}) => {
  const treeRef = useRef();
  const scene = useScene();
  const [UIData, setUIData] = useUIStore<UIData>();
  const [launcherWindowData] = useLauncherWindowStore<LauncherWindowData>();
  const [launcherElementsData] = useLauncherElementsStore<Record<string, LauncherElementData>>();

  useEffect(() => {
    if (!treeRef.current) return;

    const tree: any = treeRef.current!;
    setUIData({
      collapseElementTree: () => {
        tree.closeAll();
      },
    });
  }, [treeRef.current]);

  useEffect(() => {
    const tree: any = treeRef.current!;
    if (!tree) return;

    const lastSelectedElementID = UIData.selectedElementIDs.slice(-1)[0];
    tree.scrollTo(lastSelectedElementID, { align: 'smart' });
  }, [UIData.selectedElementIDs]);

  const onRename = ({ id, name }: any) => {
    const element = launcherElementsData[id];
    if (element.type === 'text') {
      const previousText = element.props.text;
      scene.updateElementData(id, { text: name || previousText }, 'props');
    } else {
      scene.renameElement(id, name);
    }
  };

  const onMove = ({ dragIds, parentId, index }: any) =>
    scene.moveElementsToAnotherParent({
      elementIDs: dragIds,
      parentID: parentId,
      index,
    });

  return (
    <>
      <TreeContainer>
        <Tree
          ref={treeRef}
          data={launcherWindowData.children}
          openByDefault={false}
          width="100%"
          height={height}
          rowHeight={24}
          searchTerm={searchQuery}
          searchMatch={(node, term) => {
            const element = launcherElementsData[node.data.id];
            const {
              name,
              props: { variable, commands, text } = {},
            } = element || ({ props: {} } as LauncherElementData);

            const commandText =
              commands && commands.length > 0
                ? '( ' +
                  launcherElementInfo.button.commands?.[commands?.[0].type] +
                  ' )' +
                  (commands.length > 1 ? ' +' + (commands.length - 1) : '')
                : '';
            const variableText = variable
              ? '[ ' +
                  launcherElementInfo.variableText.variables?.[variable] +
                  ' ]' || variable
              : '';
            const hrefText = element?.props?.href || '';

            const elementName =
              name ||
              variableText ||
              commandText ||
              hrefText ||
              (text && `"${text}"`);
            
            return elementName?.toLowerCase().includes(term.toLowerCase()) ? true : false;
          }}
          onRename={onRename}
          onMove={onMove}
          onClick={() => {
            scene.deselectAllElements();
            scene.endEditingElement(null);
          }}
          renderCursor={ElementTreeDragCursor}
          renderDragPreview={ElementTreeDragPreview}
        >
          {ElementTreeNode}
        </Tree>
      </TreeContainer>
    </>
  );
};

const TreeContainer = styled.div`
  width: 100%;
  height: 100%;
  padding-top: var(--padding-1);
  padding-right: calc(var(--padding-1) / 2);

  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  ::-webkit-scrollbar-track {
    background-color: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background-color: var(--neutral-3);
    border-radius: 3px;

    &:hover {
      background-color: var(--neutral-4);
    }
  }
  ::-webkit-scrollbar-corner {
    background-color: transparent;
  }

  *:not(input):focus-visible {
    outline: none;
    border: none;
  }
`;
